import { type FieldError } from 'react-hook-form';

/** A message the kit must translate, or one that is already in the operator's language. */
export type FieldErrorMessage =
  { kind: 'key'; i18nKey: string } | { kind: 'text'; text: string } | null;

/**
 * Error types whose message is shown verbatim.
 *
 * `server` is unambiguous: it is set by the server-error mapper, and those
 * messages arrive already localised (`Accept-Language` goes out on every
 * request). `custom` is **not** — Zod gives that code to every `.refine()` /
 * `.superRefine()` too, whose message is an English constant inside
 * `libs/contracts`. A field that mounts a refined schema says the translated
 * sentence itself, through `invalidMessage`; without one the message is still
 * better than nothing, so it stays here.
 */
const SERVER_TYPES = new Set(['server', 'custom']);

/**
 * Zod issue codes, mapped to kit copy.
 *
 * They are mapped rather than displayed because Zod's own messages are English
 * strings baked into the library ("String must contain at least 1
 * character(s)"). In an Arabic-first portal that is untranslated copy appearing
 * under an Arabic label — U8 forbids it, and no amount of locale switching would
 * fix it, because the string never passes through a catalogue.
 */
const ZOD_TYPE_KEYS: Readonly<Record<string, string>> = {
  too_small: 'field.tooShort',
  too_big: 'field.tooLong',
  invalid_type: 'field.required',
  invalid_format: 'field.invalidFormat',
  invalid_string: 'field.invalidFormat',
  invalid_enum_value: 'field.invalidValue',
  invalid_value: 'field.invalidValue',
};

/**
 * The same two codes, for a value whose bound is a MAGNITUDE rather than a
 * length.
 *
 * Zod says `too_big` for "more than 64 characters" and for "more than 200
 * centimetres" alike, and RHF keeps only the code — the issue's `origin` does
 * not survive into `FieldError`. So the value decides: a number was measured,
 * everything else was counted. Without this a height of 250cm against a 200cm
 * ceiling reads "that is too long", which is what P10.6f2's own preview
 * photographed (found by its verifier round).
 */
const ZOD_MAGNITUDE_KEYS: Readonly<Record<string, string>> = {
  too_small: 'field.tooSmall',
  too_big: 'field.tooLarge',
};

/**
 * And the third family: a value that is COUNTED rather than measured or spelled.
 *
 * "Choose at least two" is neither a length nor a magnitude, and borrowing the
 * length copy told a member their two-item selection was "too short".
 */
const ZOD_COUNT_KEYS: Readonly<Record<string, string>> = {
  too_small: 'field.tooFew',
  too_big: 'field.tooMany',
};

/** Whether the control is holding nothing — the only thing "required" is about. */
function isBlank(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;

  return false;
}

/**
 * What to show under a control that failed validation.
 *
 * Two things Zod's code alone cannot say, and the caller has to supply.
 *
 * **`value` decides "required" — not `isRequired`.** An empty required string
 * and a two-character minimum both arrive as `too_small`, and the field
 * definition is not what tells them apart: a *filled* required field that is
 * merely below its floor is not missing, and calling it "Required" over text the
 * operator can see is a message that contradicts the screen. So the answer is
 * required **only when the control is holding nothing**. (Reading it off
 * `isRequired` was wrong from P10, and P10.6f2's preview is where it became
 * visible: a height question with a 140cm floor answered "Required" over a
 * typed 100.)
 *
 * **`value` also decides length from magnitude** — see `ZOD_MAGNITUDE_KEYS`.
 *
 * `invalidMessage` is the field's own already-translated sentence for a
 * **refinement** — the one failure the kit structurally cannot classify, because
 * the reason lives in the contract rather than in a Zod code. It never overrides
 * the server, which answered in the operator's language to begin with.
 */
export function fieldErrorMessage(
  error: FieldError | undefined,
  isRequired: boolean,
  invalidMessage?: string,
  value?: unknown,
): FieldErrorMessage {
  if (error === undefined) {
    return null;
  }

  const type = String(error.type);

  if (type === 'custom' && invalidMessage !== undefined && invalidMessage !== '') {
    return { kind: 'text', text: invalidMessage };
  }

  if (SERVER_TYPES.has(type)) {
    return error.message === undefined || error.message === ''
      ? { kind: 'key', i18nKey: 'field.invalidValue' }
      : { kind: 'text', text: error.message };
  }

  /*
   * "Required" is about an EMPTY control — for the two codes that reach here.
   *
   * `custom`, `invalid_value` and `invalid_format` are answered by the arms
   * above, and a blank required `single_select`/`country`/`date` arrives as one
   * of those three. Deliberately not hoisted over them: the message would then
   * be the portal's own opinion rather than the api's, which is the divergence
   * this whole file exists to avoid — see PROGRESS §Known issues.
   *
   * `invalid_type` looks like it always means "there is nothing there", and the
   * first cut of this fix said so in as many words — but Zod v4 also gives it to
   * `.int()`, so a whole-number question answered `1.5` reported **"Required"**
   * over a filled box: the very defect this function was being fixed for,
   * reached one code over (found by P10.6f2's second verifier round). Gate both
   * codes on the value, and nothing can claim a filled field is missing.
   */
  if (isRequired && isBlank(value) && (type === 'invalid_type' || type === 'too_small')) {
    return { kind: 'key', i18nKey: 'field.required' };
  }

  // A filled value that is the wrong SHAPE has exactly one reachable cause in
  // this portal — a fraction where whole numbers were asked for — and it is a
  // check the kit can make rather than guess.
  if (type === 'invalid_type' && !isBlank(value)) {
    return {
      kind: 'key',
      i18nKey:
        typeof value === 'number' && !Number.isInteger(value)
          ? 'field.wholeNumber'
          : 'field.invalidValue',
    };
  }

  const family = Array.isArray(value)
    ? ZOD_COUNT_KEYS
    : typeof value === 'number'
      ? ZOD_MAGNITUDE_KEYS
      : undefined;

  return { kind: 'key', i18nKey: family?.[type] ?? ZOD_TYPE_KEYS[type] ?? 'field.invalidValue' };
}
